import { FC, type CSSProperties } from 'react';
import { V4 as C } from '@components/v4/tokens';
import { Z } from '@components/v4';
import { PaulinaChatHeader } from './PaulinaChatHeader';
import { PaulinaConversation } from './PaulinaConversation';
import { PaulinaContextValue } from './PaulinaProvider';
import { usePaulinaDialogA11y } from './usePaulinaDialogA11y';

/**
 * PaulinaSheet — el chat de Paulina como BOTTOM-SHEET en MÓVIL (Opción A). Reusa el
 * MOLDE de QuickPresentationsSheet (handle 40x5, animación `plick-sheet-up`, overlay
 * `rgba(37,55,70,.45)`, padding con `env(safe-area-inset-bottom)`, `role=dialog` +
 * `aria-modal`, Escape para cerrar, focus-trap y retorno de foco al activador — vía
 * `usePaulinaDialogA11y`) PERO a **`Z.paulinaSheet`** (95) — NUNCA el `z-140/z-250`
 * del molde original, que taparía el toast de pedido (`Z.pedido`=99). Lo operativo
 * (pedidos) SIEMPRE gana.
 *
 * Animación NUEVA respetuosa de `prefers-reduced-motion`: el keyframe `plick-sheet-up`
 * (deslizar desde abajo) solo se aplica bajo `@media (prefers-reduced-motion:
 * no-preference)`; con motion reducido el sheet aparece sin deslizamiento. Se inyecta
 * de forma idempotente (id propio) en <head>.
 */

const KF_ID = 'plick-paulina-sheet-kf-v4';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  // La animación solo corre si el usuario NO pidió reducir movimiento; con
  // prefers-reduced-motion el sheet no desliza (transform inicial neutro).
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-sheet-up{from{transform:translateY(100%)}to{transform:translateY(0)}}' +
    '.plick-paulina-sheet-anim{animation:plick-paulina-sheet-up 260ms cubic-bezier(.22,1,.36,1)}}';
  document.head.appendChild(st);
}

type PaulinaSheetProps = Pick<
  PaulinaContextValue,
  'turns' | 'input' | 'setInput' | 'busy' | 'error' | 'bottomRef' | 'goTo' | 'send' | 'setOpen' | 'clearConversation'
> & { onDeactivate?: () => void };

export const PaulinaSheet: FC<PaulinaSheetProps> = ({
  turns,
  input,
  setInput,
  busy,
  error,
  bottomRef,
  goTo,
  send,
  setOpen,
  clearConversation,
  onDeactivate,
}) => {
  const close = () => setOpen(false);
  // A11y de diálogo modal compartida: Escape, focus-trap y retorno de foco al activador.
  const dialogRef = usePaulinaDialogA11y(close);

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: Z.paulinaSheet, // SIEMPRE por debajo del toast de pedido (Z.pedido=99). NO z-140/250.
    background: 'rgba(37,55,70,.45)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  };

  const panel: CSSProperties = {
    background: C.white,
    width: '100%',
    height: '78vh',
    maxHeight: '78vh',
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(0,0,0,.28)',
    overflow: 'hidden',
    paddingBottom: 'env(safe-area-inset-bottom)',
  };

  return (
    <div style={overlay} onClick={close}>
      <div
        ref={dialogRef}
        className="plick-paulina-sheet-anim"
        style={panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Chat con Paulina"
      >
        <div style={{ display: 'grid', placeItems: 'center', padding: '8px 0 2px' }}>
          <span style={{ width: 40, height: 5, borderRadius: 9999, background: C.grayLight }} />
        </div>
        <PaulinaChatHeader
          busy={busy}
          emptyConversation={turns.length === 0}
          onClear={clearConversation}
          onClose={close}
          onDeactivate={onDeactivate}
        />
        <PaulinaConversation
          turns={turns}
          input={input}
          setInput={setInput}
          busy={busy}
          error={error}
          bottomRef={bottomRef}
          goTo={goTo}
          send={send}
        />
      </div>
    </div>
  );
};
